import { Badge, Text, Flex, Button } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Reorder } from "framer-motion";
import { FieldViewModel } from "./field.view-model";
import { useContext, useState } from "react";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { AlertsContext } from "providers/alerts";
import { SectionViewModel } from "../sections/section.view-model";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { LoanTypeViewModel } from "../loan-types/loan-type.view-model";

interface FieldsReorderProps {
  section?: SectionViewModel | null;
  loanType?: LoanTypeViewModel | null;
  fields: FieldViewModel[];
  onSaveChanges: (fields: FieldViewModel[]) => void;
  onCancel: () => void;
}

export function FieldsReorder({
  section,
  loanType,
  fields,
  onSaveChanges,
  onCancel,
}: FieldsReorderProps) {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);

  const [newFieldsOrder, setNewFieldsOrder] = useState<FieldViewModel[]>(fields);

  const [isLoading, setIsLoading] = useState(false);

  async function handleSaveChanges() {
    try {
      setIsLoading(true);
      await apiRequest("/supabase/loan_section/loan_section_fields_reorder", {
        fieldIds: newFieldsOrder.map(field => field.id),
      });

      showSuccess({
        title: "Section loan fields order has been Updated",
        description: "Section loan fields order was successfully updated",
      });

      const updatedFields = newFieldsOrder.map((field, index) => {
        field.order = index + 1;
        return field;
      });

      onSaveChanges(updatedFields);
    } catch (error) {
      showError({
        title: "Could Not update Loan Sections fields order",
        description: getErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Flex gap={3} my={4}>
        <Button
          variant="outline"
          leftIcon={<FontAwesomeIcon icon={["far", "xmark"]} />}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant={getCustomButtonVariant(user)}
          leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
          onClick={handleSaveChanges}
          isLoading={isLoading}
        >
          Save Order
        </Button>
      </Flex>

      <Reorder.Group
        values={newFieldsOrder}
        onReorder={setNewFieldsOrder}
        style={{
          listStyleType: "none",
          padding: "0px",
          margin: "0px",
          width: "100%",
        }}
      >
        {newFieldsOrder.map(field => {
          return (
            <Reorder.Item
              value={field}
              key={field.id}
              style={{
                width: "100%",
                padding: "12px",
                borderWidth: "1px",
                borderColor: "#DEDCE2",
                marginBottom: "8px",
                borderRadius: "8px",
                cursor: "grab",
                height: "57px",
                display: "grid",
                alignItems: "center",
              }}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                  <FontAwesomeIcon icon={["far", "bars"]} />
                  <Text ml="12px" fontSize="14px" color="black" flex="3">
                    {field.label}
                  </Text>
                </Flex>
                <Flex>
                  {field.required && (
                    <Badge size="sm" variant="outline" fontSize="12px" bg="white" color="gray.600">
                      Required
                    </Badge>
                  )}
                  {field.underwriting && (
                    <Badge
                      size="sm"
                      variant="outline"
                      fontSize="12px"
                      bg="white"
                      color="gray.600"
                      ml="6px"
                    >
                      Hidden
                    </Badge>
                  )}
                </Flex>
              </Flex>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
    </>
  );
}
